import { Avatar, Box, Divider, Grid, IconButton, List, ListItem, ListItemAvatar, ListItemText, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useLanguageContext } from "../../../contexts/LanguageContext";
import DefaultConstants from "../../../data/Constants";
import Dialog, { IDefaultDialogProps } from "../../Dialog";
import Empty from "../../Empty";
import IServiceOrder from "../../../interfaces/IServiceOrder";
import DateTimeHelper from "../../../helpers/DateTimeHelper";
import { ActionButton } from "../../Button";
import { IDefaultStatusData, ServiceOrderLogTypeEnum, ServicePriorityData, ServiceStatusData } from "../../../data/Enums";
import useDialog from "../../../hooks/useDialog";
import { AttachFile } from "@mui/icons-material";
import ViewAttachmentsDialog from "./ViewAttachmentsDialog";
import IServiceOrderLog from "../../../interfaces/IServiceOrderLog";
import useState from "../../../hooks/useState";


interface ILogsDialogProps extends IDefaultDialogProps {
    serviceOrder: IServiceOrder;
}


interface IStateValues {
    selectedItem: IServiceOrderLog | undefined;
}


const LogsDialog: React.FunctionComponent<ILogsDialogProps> = ({
    open,
    handleClose,
    serviceOrder
}) => {
    const { translate } = useLanguageContext();
    
    const state = useState<IStateValues>({
        selectedItem: undefined
    });

    const viewAttachmentsDialog = useDialog();


    const contentRef = useRef<HTMLDivElement | null>(null);

    const scrollContentToBottom = useCallback(() => {
        if (contentRef.current === null) {
            return;
        }

        contentRef.current.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    }, [contentRef]);


    useEffect(() => {
        viewAttachmentsDialog.handleClose();

        scrollContentToBottom();
        // eslint-disable-next-line
    }, [open, serviceOrder.logs]);


    const dialogActions = useMemo(() => {
        return [
            <ActionButton action='close' onClick={handleClose} />
        ];
        // eslint-disable-next-line
    }, [translate, serviceOrder]);


    const createStatusText = useCallback((statusId: number) => {
        const status = Object.values(ServiceStatusData).find(s => s.key === statusId) as IDefaultStatusData;
        
        return <Typography className='text-icon' sx={{ backgroundColor: `${status.color} !important` }}>
            <status.Icon />
            {translate(status.name)}
        </Typography>;
        // eslint-disable-next-line
    }, []);


    const createPriorityText = useCallback((priorityId: number) => {
        const priority = Object.values(ServicePriorityData).find(s => s.key === priorityId) as IDefaultStatusData;
        
        return <Typography className='text-icon' sx={{ backgroundColor: `${priority.color} !important` }}>
            <priority.Icon />
            {translate(priority.name)}
        </Typography>;
        // eslint-disable-next-line
    }, []);


    const createDateText = useCallback((date: string | null | undefined) => {
        if (date === undefined || date === null || date.length === 0) {
            return '-';
        }

        return DateTimeHelper.Format(date);
        // eslint-disable-next-line
    }, []);


    return <>
        <Dialog
            open={open}
            handleClose={handleClose}
            title={translate('changeLog')}
            dialogActions={dialogActions}
            className='dialogactions-flexend overflow-inherit serviceorderlogs-dialog'
            absolute={true}
            dividers={true}
            contentRef={contentRef}
        >
            <Grid 
                container 
                columnSpacing={DefaultConstants.gridColumnSpacing} 
                rowSpacing={DefaultConstants.gridRowSpacing}
                paddingY={2}
            >
                {
                    serviceOrder.logs === undefined || serviceOrder.logs.length === 0 ? <>
                        <Grid item xs={12}>
                            <Empty text="noItemsFound" />
                        </Grid>
                    </> : <>
                        <Grid item xs={12}>
                            <List>
                                {
                                    serviceOrder.logs.map((row, index) => {
                                        let typeText = undefined;
                                        let previousValueText = (row.previousValue ?? '-') as any;
                                        let newValueText = (row.newValue ?? '-') as any;
                                                
                                        if (row.type === ServiceOrderLogTypeEnum.UpdateStatus) {
                                            typeText = '_serviceOrderLogChangeStatusAction';

                                            previousValueText = createStatusText(+row.previousValue);
                                            newValueText = createStatusText(+row.newValue);
                                        } 
                                                
                                        else if (row.type === ServiceOrderLogTypeEnum.UpdatePriority) {
                                            typeText = '_serviceOrderLogChangePriorityAction';
                                                    
                                            previousValueText = createPriorityText(+row.previousValue);
                                            newValueText = createPriorityText(+row.newValue);
                                        } 
                                                
                                        else if (row.type === ServiceOrderLogTypeEnum.UpdateDepartment) {
                                            typeText = '_serviceOrderLogChangeDepartmentAction';
                                        } 
                                                
                                        else if (row.type === ServiceOrderLogTypeEnum.UpdateResponsible) {
                                            typeText = '_serviceOrderLogChangeResponsibleAction';
                                        }
                                                
                                        else if (row.type === ServiceOrderLogTypeEnum.UpdateStartDate) {
                                            typeText = '_serviceOrderLogChangeStartDateAction';
                                                    
                                            previousValueText = createDateText(row.previousValue);
                                            newValueText = createDateText(row.newValue);
                                        } 
                                                
                                        else {
                                            typeText = '_serviceOrderLogChangeEstimatedCompletionDateAction';
                                                    
                                            previousValueText = createDateText(row.previousValue);
                                            newValueText = createDateText(row.newValue);
                                        } 

                                        return <>
                                            {
                                                index > 0 && <Box paddingY={2}>
                                                    <Divider />
                                                </Box>
                                            }

                                            <ListItem key={`log-${row.id}-item-1`} disablePadding={true}>
                                                <ListItemAvatar>
                                                    <Avatar
                                                        alt={row.user.fullName.toUpperCase()} 
                                                        src={row.user.avatar} 
                                                        className='fontsize-14'
                                                    />
                                                </ListItemAvatar>

                                                <ListItemText primary={row.user.fullName} secondary={row.user.login} />

                                                <ListItemText
                                                    primary={DateTimeHelper.Format(row.updatedAt, 'DD/MM/YYYY')} 
                                                    secondary={DateTimeHelper.Format(row.updatedAt, 'HH:mm:ss')}
                                                    className='textalign-right'
                                                />
                                            </ListItem>

                                            <ListItem key={`log-${row.id}-item-2`} disablePadding={true}>
                                                <ListItemText 
                                                    primary={translate(typeText)} 
                                                    secondary={<>
                                                        <Box className='display-flex gap-12 alignitems-center'>
                                                            {translate('from').toLowerCase()}
                                                            {' '}
                                                            <strong>{previousValueText}</strong>
                                                            {' '}
                                                            {translate('to').toLowerCase()}
                                                            {' '}
                                                            <strong>{newValueText}</strong>

                                                            {
                                                                row.executionTime && <>
                                                                    ({translate('executedAt').toLowerCase()} {' '} {DateTimeHelper.ToElapsedTime(row.executionTime)})
                                                                </>
                                                            }
                                                        </Box>
                                                    </>}
                                                />

                                                {
                                                    row.attachments !== undefined && row.attachments.length > 0 && (
                                                        <IconButton 
                                                            onClick={() => {
                                                                state.update('selectedItem', row);
                                                                viewAttachmentsDialog.handleOpen();
                                                            }} 
                                                            className='borderradius-default backgroundcolor-grey-2 text-color-1 display-flex gap-8'
                                                        >
                                                            <Typography className='fontsize-14'>{row.attachments.length}</Typography>
                                                            <AttachFile fontSize="small" />
                                                        </IconButton>
                                                    )
                                                }
                                            </ListItem>
                                        </>;
                                    })
                                }
                            </List>
                        </Grid>
                    </>
                }
            </Grid>
        </Dialog>

        <ViewAttachmentsDialog
            open={viewAttachmentsDialog.open}
            handleClose={() => viewAttachmentsDialog.handleClose()}
            attachments={state.values.selectedItem?.attachments ?? []}
        />
    </>;
}

export default LogsDialog;